import logo from "@/assets/logo.png";
import { usePermissions } from "@/hooks/usePermissions";
import { cn } from "@/lib/utils";
import {
  adminNavigationGroups,
  adminRouteRegistry,
  canAccessAdminRoute,
} from "@/routes/admin-route-registry";
import { X } from "lucide-react";
import { NavLink } from "react-router-dom";

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

function Sidebar({ isOpen, onClose }: SidebarProps) {
  const { userRole, hasRole } = usePermissions();
  const navigation = adminRouteRegistry.filter(
    (route) =>
      route.navigation && canAccessAdminRoute(route, userRole, hasRole),
  );

  return (
    <>
      {isOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-950/40 backdrop-blur-sm transition-opacity duration-300 lg:hidden"
          onClick={onClose}
        />
      )}

      <aside
        className={cn(
          "fixed inset-y-0 left-0 z-50 flex h-screen w-72 transform flex-col border-r border-slate-200 bg-white transition-transform duration-300 ease-in-out lg:relative lg:translate-x-0",
          isOpen ? "translate-x-0" : "-translate-x-full",
        )}
      >
        <div className="flex shrink-0 items-center justify-between px-6 py-6">
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-slate-900 p-2">
              <img
                src={logo}
                alt="MediCorePay"
                className="h-6 w-auto object-contain brightness-0 invert"
              />
            </div>
            <div>
              <h1 className="text-lg font-black leading-none tracking-tight text-slate-900">
                Admin
              </h1>
              <p className="mt-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Revival Console
              </p>
            </div>
          </div>
          <button
            type="button"
            aria-label="Close navigation"
            onClick={onClose}
            className="p-2 text-slate-400 transition-colors hover:text-slate-900 lg:hidden"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <nav className="min-h-0 flex-1 space-y-5 overflow-y-auto px-4 py-2">
          {adminNavigationGroups.map((group) => {
            const items = navigation.filter(
              (route) => route.navigation?.group === group,
            );
            if (!items.length) return null;
            return (
              <div key={group} className="space-y-1">
                <p className="px-4 pb-1 text-[10px] font-black uppercase tracking-[0.2em] text-slate-400">
                  {group}
                </p>
                {items.map((route) => {
                  const item = route.navigation!;
                  const Icon = item.icon;
                  return (
                    <NavLink
                      key={route.path}
                      to={route.path}
                      end={item.end}
                      className={({ isActive }) =>
                        cn(
                          "group flex items-center gap-3 rounded-xl px-4 py-2.5 text-sm font-medium transition-all duration-200",
                          isActive
                            ? "bg-slate-900 text-white shadow-lg shadow-slate-900/20"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900",
                        )
                      }
                      onClick={() => {
                        if (window.innerWidth < 1024) onClose();
                      }}
                    >
                      <Icon className="h-4 w-4 transition-transform group-hover:scale-110" />
                      <span>{item.label}</span>
                    </NavLink>
                  );
                })}
              </div>
            );
          })}
        </nav>

        <div className="shrink-0 p-4">
          <div className="rounded-2xl border border-slate-100 bg-slate-50 p-4">
            <div className="mb-2 flex items-center gap-2">
              <div className="h-2 w-2 rounded-full bg-emerald-500" />
              <p className="text-[10px] font-black uppercase tracking-widest text-slate-600">
                Restricted console
              </p>
            </div>
            <p className="text-xs font-medium leading-relaxed text-slate-500">
              Capability overrides and support access require elevated roles and
              an audit reason.
            </p>
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
